import PropTypes from 'prop-types';

import { COLUMN_SEARCH_TYPES } from '@constants/index';
import { reportTransactionTypes } from '@constants/correspondent';
import DatePicker from '@shared/components/DatePicker/DatePicker';
import CashierSelector from '@shared/components/selectors/CashierSelector';
import SubsidiaryFilterSelector from '@shared/components/selectors/SubsidiaryFilterSelector';
import TableView from '@shared/components/table';
import { PrimaryButton } from '@shared/components/ui';
import { useCurrentUser } from '@shared/hooks';
import { TRANSACTIONS_REPORT_TABLE_HEADERS } from '@shared/tableHeaders/transactionsReport';
import { getLabel } from '@shared/utils';
import { useTranslation } from 'react-i18next';

const columnsSearch = (onSearch, error, search, t, subsidiary, roleName) => [
  {
    name: 'correspondent_name',
    label: getLabel('correspondent'),
    type: COLUMN_SEARCH_TYPES.TEXT,
    onSearch,
    inputProps: {
      maxLength: 50,
    },
    errorMessages: {
      maxLength: t('transactions.filters.errors.maxLength'),
    },
    filter: {
      [reportTransactionTypes.analystCorrespondent]: true,
    },
  },
  {
    name: 'date_from',
    component: (
      <DatePicker
        key={'fromDate'}
        label={getLabel('fromDate')}
        name="date_from"
        onChange={onSearch}
        maxDate={new Date().toISOString()}
        error={error?.date?.error || false}
        helperText={error?.date?.helperText ?? ''}
        value={search?.date_from || new Date().toISOString()}
      />
    ),
    filter: {
      [reportTransactionTypes.adminCorrespondent]: true,
      [reportTransactionTypes.correspondentConsultant]: true,
      [reportTransactionTypes.analystCorrespondent]: true,
    },
  },
  {
    component: (
      <DatePicker
        key={'toDate'}
        label={getLabel('toDate')}
        name="date_to"
        onChange={onSearch}
        minDate={search?.date_from}
        maxDate={new Date().toISOString()}
        error={error?.date?.error || false}
        helperText={error?.date?.helperText ?? ''}
        value={search?.date_to || new Date().toISOString()}
      />
    ),
    filter: {
      [reportTransactionTypes.adminCorrespondent]: true,
      [reportTransactionTypes.correspondentConsultant]: true,
      [reportTransactionTypes.analystCorrespondent]: true,
    },
  },
  {
    name: 'transaction_id',
    label: getLabel('transaction'),
    onSearch,
    type: COLUMN_SEARCH_TYPES.NUMBER,
    inputProps: {
      maxLength: 15,
    },
    errorMessages: {
      maxLength: t('transactions.filters.errors.maxLength'),
    },
    filter: {
      [reportTransactionTypes.adminCorrespondent]: true,
      [reportTransactionTypes.correspondentConsultant]: true,
      [reportTransactionTypes.analystCorrespondent]: true,
    },
  },
  {
    component: (
      <SubsidiaryFilterSelector
        key="subsidiaryFilterSelector"
        value={search?.subsidiary_id}
        onSearch={onSearch}
      />
    ),
    filter: {
      [reportTransactionTypes.adminCorrespondent]: true,
      [reportTransactionTypes.correspondentConsultant]: true,
      [reportTransactionTypes.analystCorrespondent]: true,
    },
  },
  {
    component: (
      <CashierSelector key="cashierSelector" subsidiary={subsidiary} onSearch={onSearch} roleName={roleName} />
    ),
    filter: {
      [reportTransactionTypes.adminCorrespondent]: true,
      [reportTransactionTypes.correspondentConsultant]: true,
      [reportTransactionTypes.analystCorrespondent]: true,
    },
  },
  {
    name: 'client_phone',
    label: getLabel('phone'),
    onSearch,
    type: COLUMN_SEARCH_TYPES.NUMBER,
    inputProps: {
      maxLength: 8,
    },
    errorMessages: {
      maxLength: t('transactions.filters.errors.maxLength'),
    },
    filter: {
      [reportTransactionTypes.adminCorrespondent]: true,
      [reportTransactionTypes.correspondentConsultant]: true,
      [reportTransactionTypes.analystCorrespondent]: true,
    },
  },
];

const TransactionsTable = ({
  transactions = [],
  count = 0,
  currentPage = 0,
  onPageChange = () => {},
  onSearch = () => {},
  loading = false,
  search = {},
  error = {},
  filterData = '',
  onPageSizeChange = () => {},
  onSortChange = () => {},
  onManualSearch,
}) => {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const { subsidiary, role_name: roleName } = user;
  const searchColumns = columnsSearch(onSearch, error, search, t, subsidiary, roleName).filter(
    (column) => column.filter?.[filterData]
  );

  if (onManualSearch) {
    searchColumns.push({
      name: 'manual_search',
      component: (
        <PrimaryButton
          key="manual-search"
          type="button"
          variant="contained"
          onClick={onManualSearch}
          aria-label={getLabel('search')}
          sx={{ alignSelf: 'center', height: 56, px: 3 }}>
          {getLabel('search')}
        </PrimaryButton>
      ),
    });
  }

  return (
    <TableView
      isLoading={loading}
      pagination={{
        pageNumber: currentPage,
        handleChangePage: onPageChange,
        handleChangeRowsPerPage: onPageSizeChange,
        rowsTotalCount: Number(count),
      }}
      columnsSearch={searchColumns}
      columnSearchDelay={onManualSearch ? 0 : 500}
      headers={TRANSACTIONS_REPORT_TABLE_HEADERS.filter((header) => header.filter?.[filterData])}
      rows={transactions}
      mapRowData={(row) => ({ ...row })}
      onSortingChange={onSortChange}
    />
  );
};

TransactionsTable.propTypes = {
  transactions: PropTypes.arrayOf(
    PropTypes.shape({
      transfer_date: PropTypes.string,
      transfer_hour: PropTypes.string,
      transaction_id: PropTypes.string,
      correspondent_name: PropTypes.string,
      subsidiary_name: PropTypes.string,
      cashier_name: PropTypes.string,
      transfer_type: PropTypes.string,
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      client_phone: PropTypes.string,
      status: PropTypes.string,
      state_name: PropTypes.string,
      city_name: PropTypes.string,
    })
  ).isRequired,
  search: PropTypes.object.isRequired,
  count: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.object,
  filterData: PropTypes.string,
  onPageSizeChange: PropTypes.func,
  onSortChange: PropTypes.func,
  onManualSearch: PropTypes.func,
};

export default TransactionsTable;
